import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ShoppingBag, Sparkles, ArrowRight, BadgeCheck, Loader2, AlertCircle, Package, X,
} from "lucide-react";

const REDIRECT_SECONDS = 5;
const PLATFORM_LABELS = {
  tiktok_shop: "TikTok Shop", shopee: "Shopee Affiliate", tokopedia: "Tokopedia", lazada: "Lazada",
};

const targetUrl = (talent, platform) => {
  if (talent?.link?.product_url) return talent.link.product_url;
  if (platform === "tiktok_shop" && talent?.tiktok_handle) return `https://www.tiktok.com/${talent.tiktok_handle}`;
  return null;
};

function RedirectBar({ seconds, onCancel }) {
  return (
    <div className="space-y-2" data-testid="referral-redirect-bar">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="referral-redirect-countdown">Mengarahkan otomatis dalam <b className="text-foreground font-mono">{seconds}</b> detik...</span>
        <button data-testid="referral-cancel-redirect-button" onClick={onCancel} className="flex items-center gap-1 hover:text-foreground">
          <X className="h-3 w-3" /> Batalkan
        </button>
      </div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
        <motion.div className="h-full bg-primary" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: REDIRECT_SECONDS, ease: "linear" }} />
      </div>
    </div>
  );
}

export default function ReferralPage() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const platform = searchParams.get("platform") || "tiktok_shop";
  const campaignSlug = searchParams.get("c");
  const [talent, setTalent] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const [autoRedirect, setAutoRedirect] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const params = campaignSlug ? { c: campaignSlug } : {};
    (async () => {
      try {
        const { data } = await api.get(`/ref/${code}`, { params });
        if (cancelled) return;
        setTalent(data);
        api.post(`/ref/${code}/click`, null, { params }).catch(() => {});
      } catch {
        if (!cancelled) setError("Link afiliasi tidak ditemukan atau sudah tidak aktif.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, campaignSlug]);

  const platformLabel = PLATFORM_LABELS[platform] || "Marketplace";
  const destination = targetUrl(talent, platform);
  const counting = Boolean(destination) && autoRedirect;

  useEffect(() => {
    if (!counting) { return undefined; }
    if (seconds <= 0) {
      window.location.href = destination;
      return undefined;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [counting, seconds, destination]);

  const goNow = () => {
    if (destination) window.open(destination, "_blank", "noopener");
    setAutoRedirect(false);
  };

  return (
    <div className="min-h-screen grid-glow flex flex-col" data-testid="referral-page-container">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Komisia" className="h-9 w-9 rounded-lg" />
            <p className="font-display font-extrabold">Komisia</p>
          </Link>
          <Link to="/login?daftar=1">
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-white font-semibold">Daftar Jadi Talent</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-16">
        {loading && (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm">Memuat link afiliasi...</p>
          </div>
        )}

        {!loading && error && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-card border border-destructive/30 rounded-2xl p-8 text-center" data-testid="referral-error-card">
            <div className="h-14 w-14 rounded-full bg-destructive/15 grid place-items-center mx-auto mb-4">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="font-display text-xl font-bold">Link Tidak Ditemukan</h1>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Link to="/"><Button className="mt-6 bg-primary hover:bg-primary/90 text-white">Kembali ke Beranda</Button></Link>
          </motion.div>
        )}

        {!loading && talent && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full text-center space-y-8" data-testid="referral-content">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5" /> Link Afiliasi Terverifikasi Komisia
            </div>

            <div>
              <img
                src={talent.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(talent.name)}&background=6366F1&color=fff`}
                alt={talent.name}
                className="h-24 w-24 rounded-full object-cover border-2 border-primary/40 mx-auto shadow-xl shadow-primary/20"
              />
              <h1 className="font-display text-3xl font-extrabold tracking-tight mt-5 flex items-center justify-center gap-2" data-testid="referral-creator-name">
                {talent.name}
                <BadgeCheck className="h-6 w-6 text-cyan-400" />
              </h1>
              <p className="text-muted-foreground mt-2">
                {talent.tiktok_handle && <span className="font-mono text-cyan-400">{talent.tiktok_handle}</span>} • Kreator Afiliasi {talent.tier}
              </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              {talent.link && (
                <div className="flex items-center gap-3 bg-background rounded-xl p-3 text-left" data-testid="referral-product-card">
                  <div className="h-11 w-11 rounded-lg bg-primary/15 grid place-items-center shrink-0"><Package className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground">Produk rekomendasi{talent.link.campaign ? ` • ${talent.link.campaign}` : ""}</p>
                    <p className="font-semibold truncate" data-testid="referral-product-name">{talent.link.product_name}</p>
                  </div>
                </div>
              )}
              <p className="text-sm text-muted-foreground leading-relaxed">
                Kamu diarahkan dari link resmi kreator ini. Belanja lewat link ini membantu kreator mendapatkan komisi — tanpa biaya tambahan untuk kamu.
              </p>
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <span className="px-2.5 py-1 rounded-md bg-secondary border border-border font-medium">{platformLabel}</span>
                <span className="font-mono">ref/{talent.affiliate_code}{talent.link ? `/${talent.link.slug}` : ""}</span>
              </div>
              <Button data-testid="referral-shop-cta-button" onClick={goNow} className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold text-base">
                <ShoppingBag className="h-5 w-5 mr-2" /> Lanjut Belanja di {platformLabel}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              {counting && <RedirectBar seconds={seconds} onCancel={() => setAutoRedirect(false)} />}
              <p className="text-[11px] text-muted-foreground">
                Transaksi diselesaikan di aplikasi {platformLabel}. Klik ini tercatat pada sistem tracking Komisia.
              </p>
            </div>

            <div className="bg-gradient-to-r from-primary/10 via-card to-card border border-primary/25 rounded-2xl p-6">
              <p className="font-display font-bold">Ingin dapat komisi seperti {talent.name.split(" ")[0]}?</p>
              <p className="text-sm text-muted-foreground mt-1 mb-4">Gabung Komisia — tracking penjualan otomatis & pencairan komisi cepat via Easylink.</p>
              <Link to="/login?daftar=1">
                <Button data-testid="referral-join-cta-button" variant="outline" className="border-primary/40 text-primary hover:bg-primary/10">Daftar sebagai Talent</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © 2026 Komisia — Platform Afiliasi & Pencairan Komisi
      </footer>
    </div>
  );
}
